import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, ViewChild, Renderer2 } from '@angular/core';
import { FormsModule } from '@angular/forms';
import anime from 'animejs/lib/anime.es.js';

import { DeviceService } from 'app/services/device.service';
import { UserService } from 'app/services/user.service';
import { getFriendlyRoute } from 'app/utils/flight/flight-data-utils';
import { FlightConnectionsService } from 'app/services/flight-connections-services';
import { FlightConnectionChatService } from 'app/services/flight-connection-chat.service';

import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { PopTrayComponent } from 'app/components/ui-kit/popovers/pop-tray/pop-tray.component';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { BaseTextAreaComponent } from 'app/components/ui-kit/inputs/base-text-area/base-text-area.component';
import { StarRatingComponent } from 'app/components/ui-kit/inputs/star-rating/star-rating.component';
import { UserInteractionService } from 'app/services/user-interaction.service';

import { imgIxParams } from 'app/constants/img-ix-params';
import { UserTypingComponent } from "../../../../../../../components/distinct-components/animations/user-typing/user-typing.component";
import { ChatMessageComponent } from "./components/chat-message/chat-message.component";
import { UserFlagsService } from 'app/services/user-flags.service';
import { Router } from '@angular/router';
import { normalizeProvider } from 'app/constants/provider-constants';

@Component({
  selector: 'app-connection-chat',
  standalone: true,
  imports: [CommonModule, SvgLoaderComponent, BaseInputComponent, FormsModule, SpinnerComponent, UserTypingComponent, ChatMessageComponent, PopTrayComponent, BaseTextAreaComponent, StarRatingComponent, PrimaryBtnComponent],
  templateUrl: './connection-chat.component.html',
  styleUrl: './connection-chat.component.scss'
})
export class ConnectionChatComponent {
  @Input() connection: any;
  @Input() flightOption: any;

  @ViewChild('messagesWrapper', { static: false }) messagesElRef: ElementRef<HTMLElement>;
  @ViewChild('menu') menu!: ElementRef;

  isLoading: boolean = true;
  isLoadingUserData: boolean = true;
  connectionSub: any;

  friendlyRoute: any;
  otherUser: any;
  otherOptimizedProfileImgURL: string = null;
  user: any;
  optimizedProfileImgURL: string = null;
  displayMobileNumber: string | null = null;

  deviceType: string;
  private initialViewportHeight = 0;
  private keyboardCheckInterval: any;
  private debouncedViewportResize = this.debounce(
    this.handleViewportResize.bind(this),
    200
  );
  isKeyboardFocused: boolean = false;
  isKeyboardActive: boolean = false;
  isKeyboardShowing: boolean = false;
  isKeyboardDoneShowing: boolean = false;
  isCurrentlyTyping: boolean = false;
  isOtherUserTyping: boolean = false;
  messages: any[] = [];
  private messageUnsub: () => void;
  newMessage: string = '';

  showSystemGuide: boolean = true;
  showShareTray: boolean = false;
  hasSharedPhone: boolean = false;
  shareActions: any;
  private flagSub: any;
  menuListener: any;

  systemMessageCase: 'same' | 'different' | 'independent' = 'same';

  showMenu: boolean = false;
  showDisconnectTray: boolean = false;
  showBlockTray: boolean = false;
  showReviewTray: boolean = false;

  disconnectActions: any;
  blockActions: any;
  reviewActions: any;

  blockReason: string = '';
  reviewRating: number = 5;
  reviewText: string = '';
  userHasReviewed: boolean = false;

  private typingTimeout: any;
  private TYPING_DEBOUNCE_MS = 5000;

  private getPrimaryProvider(u: any): string | null {
    const membership = Array.isArray(u?.memberships)
      ? u.memberships.find((m: any) => m?.provider)
      : null;
    if (!membership) {
      return null;
    }
    return normalizeProvider(membership.provider);
  }

  constructor(
    private userService: UserService,
    private deviceService: DeviceService,
    private connectionsService: FlightConnectionsService,
    private chatService: FlightConnectionChatService,
    private flagsService: UserFlagsService,
    private userInteraction: UserInteractionService,
    private renderer: Renderer2,
    private router: Router,
  ) { }

  ngOnInit(): void {

    this.getUsersData();
    this.translateRoute();
    this.subscribeToMessages();
    this.subscribeToDoc();
    this.deviceType = this.deviceService.getDeviceType();

    this.shareActions = {
      primaryButtons: [
        { text: 'Share this Time', function: () => this.handleShareOnce() },
        { text: 'Always Share with Connections', function: () => this.handleAlwaysShare() },
      ],
      secondaryButtons: [
        { text: 'Cancel', function: () => this.onClosePopTray() },
      ],
      closeFunction: () => this.onClosePopTray()
    };

    this.disconnectActions = {
      primaryButtons: [
        { text: 'Disconnect', function: () => this.handleDisconnect() },
      ],
      secondaryButtons: [
        { text: 'Cancel', function: () => this.onClosePopTray() },
      ],
      closeFunction: () => this.onClosePopTray(),
    };

    this.blockActions = {
      primaryButtons: [
        { text: 'Block User', function: () => this.handleBlockUser() },
      ],
      secondaryButtons: [
        { text: 'Cancel', function: () => this.onClosePopTray() },
      ],
      closeFunction: () => this.onClosePopTray(),
    };

    this.reviewActions = {
      primaryButtons: [
        { text: 'Submit Review', function: () => this.handleSubmitReview() },
      ],
      secondaryButtons: [
        { text: 'Cancel', function: () => this.onClosePopTray() },
      ],
      closeFunction: () => this.onClosePopTray(),
    };

    this.flagSub = this.flagsService.flags$.subscribe(flags => {
      if (flags['alwaysSharePhoneWithConnection']) {
        this.hasSharedPhone = true;
      }
    });

    if (window.visualViewport) {
      this.initialViewportHeight = window.visualViewport.height;
      window.visualViewport.addEventListener('resize', this.debouncedViewportResize);
    } else {
      this.initialViewportHeight = window.innerHeight;
      this.keyboardCheckInterval = setInterval(() => {
        const heightDiff = this.initialViewportHeight - window.innerHeight;
        const keyboardIsNowVisible = heightDiff > 150;

        if (keyboardIsNowVisible !== this.isKeyboardActive) {
          this.isKeyboardActive = keyboardIsNowVisible;
          this.isKeyboardActive ? this.onKeyboardShow() : this.onKeyboardHide();
        }
      }, 300);
    }
  }

  subscribeToMessages() {
    if (this.connection?.id) {
      this.messageUnsub = this.chatService.subscribeToMessages(
        this.connection.id,
        (messages) => {
          this.messages = messages;
          this.isLoading = false;
          setTimeout(() => {
            this.handleScrollToLastMessage();
            this.chatService.markNotificationAsSeen(this.connection.id);
          }, 200);
        }
      );
    }
  }

  subscribeToDoc() {
    this.connectionSub = this.connectionsService.observeConnectionById(this.connection.id).subscribe(connection => {
      this.connection = connection;
      this.isOtherUserTyping = connection?.usersTyping?.includes(this.otherUser?.id) || false;
      if (this.isOtherUserTyping) {
        this.handleScrollToLastMessage();
      }
      if (connection?.phoneSharedUsers?.includes(this.user?.id)) {
        this.hasSharedPhone = true;
      }
    });
  }

  async getUsersData() {
    const currentUser = await this.userService.getUserProfile();
    this.user = currentUser;
    this.displayMobileNumber = currentUser?.mobileNumberFriendly || currentUser?.mobileNumber || '';
    if (this.connection?.phoneSharedUsers?.includes(currentUser.id)) {
      this.hasSharedPhone = true;
    }
    if (currentUser?.profileImgURL) {
      this.optimizedProfileImgURL = currentUser.profileImgURL + imgIxParams.profile.xs;
    }
    const otherUserId = this.connection.fromId === currentUser.id ? this.connection.toId : this.connection.fromId;
    const otherUserProfile = await this.userService.getProfileById(otherUserId);
    this.otherUser = otherUserProfile;
    if (otherUserProfile?.profileImgURL) {
      this.otherOptimizedProfileImgURL = otherUserProfile.profileImgURL + imgIxParams.profile.xs;
    }
    const alreadyReviewed = await this.userInteraction.hasUserBeenReviewed(this.otherUser.id, this.connection.id);
    this.userHasReviewed = alreadyReviewed;
    this.isLoadingUserData = false;

    const userProvider = this.getPrimaryProvider(this.user);
    const otherProvider = this.getPrimaryProvider(this.otherUser);

    if (!userProvider || !otherProvider || userProvider === 'independent' || otherProvider === 'independent') {
      this.systemMessageCase = 'independent';
    } else if (userProvider === otherProvider) {
      this.systemMessageCase = 'same';
    } else {
      this.systemMessageCase = 'different';
    }
  }

  translateRoute() {
    const from = this.connection.fromLocation;
    const to = this.connection.toLocation;
    let loc = to;
     if (to === 'Beach' || to === 'Mountain') {
      loc = `Any ${to}`;
    }
    const route = getFriendlyRoute(from, { locationName: loc });
    this.friendlyRoute = route;
  }

  async onSendMessage() {
    clearTimeout(this.typingTimeout);
    this.isCurrentlyTyping = false;
    this.chatService.setTypingState(this.connection.id, this.user.id, false);
    const text = this.newMessage.trim();
    if (!text || !this.connection?.id) return;

    await this.chatService.sendMessage(this.connection.id, text, null, this.user.displayName, this.otherUser.id); // null is for reply to id later
    this.newMessage = '';
    this.handleScrollToLastMessage();
  }

  onBackdropClick() {
    this.onBlur();
  }

  onBlur() {
    if (this.deviceType === 'mobile') {
      this.isKeyboardFocused = false;
    }
  }

  onFocus() {
    if (this.isKeyboardFocused === false && this.deviceType === 'mobile') {
      this.isKeyboardFocused = true;
    }
  }

  onTyping() {
    if (!this.isCurrentlyTyping) {
      this.isCurrentlyTyping = true;
      this.chatService.setTypingState(this.connection.id, this.user.id, true);
    }

    if (this.typingTimeout) clearTimeout(this.typingTimeout);

    this.typingTimeout = setTimeout(() => {
      this.isCurrentlyTyping = false;
      this.chatService.setTypingState(this.connection.id, this.user.id, false);
    }, this.TYPING_DEBOUNCE_MS);
  }

  onShareNumberClick() {
    this.showShareTray = true;
  }

  private async postShareMessage() {
    if (!this.connection?.id) return;
    const phoneNumber = this.displayMobileNumber || this.user?.mobileNumber || '';
    const text = `${this.user.displayName} has shared their phone number (${phoneNumber}) and prefers to coordinate via text`;
    await this.chatService.sendMessage(this.connection.id, text, null, this.user.displayName, this.otherUser.id);
    await this.connectionsService.markPhoneNumberShared(this.connection.id, this.user.id);
  }

  async handleShareOnce() {
    await this.postShareMessage();
    this.showShareTray = false;
    this.hasSharedPhone = true;
  }

  async handleAlwaysShare() {
    await this.postShareMessage();
    await this.flagsService.setFlag('alwaysSharePhoneWithConnection');
    this.showShareTray = false;
    this.hasSharedPhone = true;
  }

  onClosePopTray() {
    this.showShareTray = false;
    this.showDisconnectTray = false;
    this.showBlockTray = false;
    this.showReviewTray = false;
    this.blockReason = '';
    this.reviewText = '';
    this.reviewRating = 5;
    this.showMenu = false;
  }

  private handleViewportResize(): void {
    const heightDiff = this.initialViewportHeight - window.visualViewport.height;
    const keyboardIsNowVisible = heightDiff > 150;
    if (keyboardIsNowVisible !== this.isKeyboardActive) {
      this.isKeyboardActive = keyboardIsNowVisible;
      this.isKeyboardActive ? this.onKeyboardShow() : this.onKeyboardHide();
    }
  }

  private onKeyboardShow(): void {
    this.isKeyboardActive = true;
    this.isKeyboardDoneShowing = true;
    this.adjustExchangeWrapperHeight();
  }

  private onKeyboardHide(): void {
    this.isKeyboardActive = false;
    anime({
      targets: '#ex-wrap',
      translateY: [6, 0],
      duration: 1000,
      easing: 'easeOutSine',
    });
    this.resetExchangeWrapperHeight();
  }

  private adjustExchangeWrapperHeight(): void {
    const exchangeEl = this.messagesElRef?.nativeElement;
    if (window.visualViewport && exchangeEl) {
      const viewportHeight = window.visualViewport.height;
      const inputWrapperHeight = 97;
      const adjustedHeight = viewportHeight - inputWrapperHeight;

      exchangeEl.style.height = `${adjustedHeight}px`;
      this.handleScrollToLastMessage();

    }
  }

  private resetExchangeWrapperHeight(): void {
    const exchangeEl = this.messagesElRef?.nativeElement;
    this.isKeyboardDoneShowing = false;
    if (exchangeEl) {
      exchangeEl.style.height = "";
      this.handleScrollToLastMessage();
    }
  }

  private handleScrollToLastMessage() {
    const el = this.messagesElRef?.nativeElement;
    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'auto'
      });
    });
  }

  isFlightReadyToReview() {
    if (!this.flightOption) return false;

    if (this.userHasReviewed) return false;

    const dateStr = this.flightOption.departureDateExact || this.flightOption.departureDateRangeEnd;
    if (!dateStr) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const flightDate = new Date(dateStr);
    flightDate.setHours(23, 59, 59, 999);

    return flightDate < today;
  }

  onToggleMenu() {
    if (!this.showMenu) {
      this.showMenu = true;

      this.menuListener?.();

      setTimeout(() => {
        this.menuListener = this.renderer.listen('document', 'click', (e: Event) => {
          if (this.menu && !this.menu.nativeElement.contains(e.target)) {
            this.showMenu = false;
            this.menuListener?.();
            this.menuListener = null; // Prevent stale reference
          }
        });
      }, 50);
      this.showMenu = true;
    } else {
      this.showMenu = false;
      this.menuListener?.();
    }
  }

  showDisconnect() {
    this.showMenu = false;
    this.showDisconnectTray = true;
  }

  showBlock() {
    this.showMenu = false;
    this.showBlockTray = true;
  }

  showReview() {
    this.showReviewTray = true;
  }

  async handleDisconnect() {
    try {
      const friendlyRoute = getFriendlyRoute(this.connection.fromLocation, { locationName: this.connection.toLocation });
      await this.connectionsService.cancelConnection(this.connection.id, this.user.displayName, this.otherUser.id, friendlyRoute);
    } catch (err) {
      console.error('Failed to disconnect', err);
    } finally {
      this.onClosePopTray();
      this.router.navigate(['/connections']);
    }
  }

  async handleBlockUser() {
    try {
      const friendlyRoute = getFriendlyRoute(this.connection.fromLocation, { locationName: this.connection.toLocation });
      await this.connectionsService.cancelConnection(this.connection.id, this.user.displayName, this.otherUser.id, friendlyRoute);
      await this.userInteraction.blockUser(this.otherUser.id, this.connection.id, this.blockReason);
    } catch (err) {
      console.error('Failed to block user', err);
    } finally {
      this.onClosePopTray();
      this.router.navigate(['/connections']);
    }
  }

  async handleSubmitReview() {
    try {
      await this.userInteraction.reviewUser(this.otherUser.id, this.connection.id, this.reviewRating, this.reviewText);
      this.userHasReviewed = true;
    } catch (err) {
      console.error('Failed to submit review', err);
    } finally {
      this.onClosePopTray();
    }
  }

  private debounce<T extends (...args: any[]) => void>(func: T, wait: number): T {
    let timeout: any;
    return function (...args: any[]) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    } as T;
  }

  ngOnDestroy(): void {
    this.messageUnsub?.();
    this.connectionSub.unsubscribe();
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.debouncedViewportResize);
    }
    if (this.keyboardCheckInterval) {
      clearInterval(this.keyboardCheckInterval);
    }
    this.flagSub?.unsubscribe();
    this.menuListener?.();
  }
}
